import { getServiceAwardByYears } from '../awards/serviceAwards';
import { metalForYears } from '../awards/awardMetals';
import { parseHireDate } from '../../utils/hireDate';

/**
 * Tenure, as the directory row shows it.
 *
 * What used to live here: an eight-tier emoji ladder (🌱 ⭐ 🥈 🥇 💎 🏆 👑 🧙) rendered
 * as a stack of up to three 36px circles mid-row, plus a TenureIndicator that drew
 * the same emoji at 24px. Both are gone — they were decoration rather than data,
 * needed a legend to read, and rendered differently on every OS. Nothing imported
 * either of them any more; the award objects live in awards/AwardBadge.jsx and the
 * row shows a numeral.
 */

/**
 * Calculate years of tenure from hire date
 */
export function calculateTenure(hireDate) {
  if (!hireDate) return 0;
  const hire = parseHireDate(hireDate);
  if (!hire) return 0;
  const now = new Date();
  const years = Math.floor((now - hire) / (365.25 * 24 * 60 * 60 * 1000));
  return Math.max(0, years);
}

/**
 * Which service RANK a tenure lands in — the named era (Veteran, Luminary...).
 * The metal (and hence the rail colour below) is a separate, finer scale keyed
 * directly off years — see awards/awardMetals.js.
 */
export function getTenureTier(years) {
  const award = getServiceAwardByYears(years);
  return { tier: award.tier, label: award.label, name: award.name };
}

/**
 * Tenure as a numeral for the directory list row.
 *
 * Replaces the stack of up to three 36px emoji tier badges that used to sit
 * mid-row: those were decoration rather than data, needed a legend to read, and
 * rendered differently on every OS. One monospace chip with a tier rail says the
 * same thing in a fixed 56px column. A two-tone metal stop (see awardMetals.js)
 * gets a double rail — the chip-scale echo of the crest's dual ring.
 */
export function TenureChip({ hireDate, onClick, className = '' }) {
  // Hire dates come from Vantagepoint via an async merge that fails silently
  // (getDeltekEmployees catches and returns an empty map). Render an em dash
  // rather than nothing, so an unpopulated column reads as "not known yet"
  // instead of looking like a broken feature.
  if (!hireDate) {
    return <span className="text-xs text-slate-400 dark:text-zinc-600 select-none">—</span>;
  }

  const years = calculateTenure(hireDate);
  const { name } = getTenureTier(years);
  const metal = metalForYears(years);

  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      onClick={onClick ? (e) => { e.stopPropagation(); onClick(e); } : undefined}
      title={years < 1 ? `Less than a year · ${name}` : `${years} ${years === 1 ? 'year' : 'years'} · ${name}`}
      className={`inline-flex items-center gap-1.5 h-[22px] pl-1.5 pr-2 rounded-md border
        font-mono text-[11px] font-semibold tabular-nums leading-none bg-surface-chip border-line-chip text-ink-2
        ${onClick ? 'hover:border-accent transition-colors cursor-pointer' : ''}
        ${className}`}
    >
      {metal.accent ? (
        <span className="flex-none flex items-center gap-[1px]" aria-hidden="true">
          <span className="w-[3px] h-3 rounded-sm" style={{ background: `var(--${metal.base}-ring)` }} />
          <span className="w-[2px] h-3 rounded-sm" style={{ background: `var(--${metal.accent}-ring)` }} />
        </span>
      ) : (
        <span
          className="flex-none w-[3px] h-3 rounded-sm"
          style={{ background: `var(--${metal.base}-ring)` }}
          aria-hidden="true"
        />
      )}
      {years < 1 ? '<1y' : `${years}y`}
    </Tag>
  );
}
